import React, { useCallback, useState } from "react";
import { IAcademyCourse } from "src/academy/types";
import "./AcademyCourseSidebarStatus.scss";
import { localize } from "src/l10n";
import { Label } from "src/ui";
import AcademyCourseProgress from "../../AcademyCourseProgress/AcademyCourseProgress";
import { DefaultButton, PrimaryButton, TooltipHost } from "@fluentui/react";
import { useDispatch, useSelector } from "react-redux";
import { setConfirmPopup } from "src/popups/actions";
import api from "src/spintr/SpintrApi";
import SpintrLoader from "src/ui/components/Loader";
import { Link } from "react-router-dom";

interface IProps {
    course: IAcademyCourse;
    onOpenCourse: () => void;
    onJoinCourse: () => void;
    onCancelCourse: () => void;
    displayPreview?: boolean;
    onReviewCourse: () => void;
}

interface IState {
    isLoadingAttendance: boolean;
    expiredCourse: boolean;
}

const AcademyCourseSidebarStatus = (props: IProps) => {
    const dispatch = useDispatch();

    const [state, setState] = useState<IState>({
        isLoadingAttendance: false,
        expiredCourse: props.course.publishEndDate && new Date() > new Date(props.course.publishEndDate),
    });

    const isAdminOrEditor = useSelector<Spintr.AppState, boolean>((state) => state.profile.active.isAdmin || state.profile.active.isEditor);
    const appMode = useSelector<Spintr.AppState, boolean>((state) => state.ui.appMode);

    const onAttendClick = useCallback(() => {
        setState((s: IState) => ({
            ...s,
            isLoadingAttendance: true
        }));

        api.post("/api/v1/academy/courses/" + props.course.id + "/attend").then(() => {
            setState((s: IState) => ({
                ...s,
                isLoadingAttendance: false
            }));

            props.onJoinCourse();
        }).catch(() => { });
    }, []);

    const onCancelClick = useCallback(() => {
        dispatch(setConfirmPopup({
            isOpen: true,
            message: localize("CANCEL_COURSE_WARNING"),
            onConfirm: () => {
                setState((s: IState) => ({
                    ...s,
                    isLoadingAttendance: true
                }));

                api.delete("/api/v1/academy/courses/" + props.course.id + "/cancel").then(() => {
                    setState((s: IState) => ({
                        ...s,
                        isLoadingAttendance: false
                    }));

                    props.onCancelCourse();
                }).catch(() => { });
            }
        }));
    }, []);

    return (
        <div className="AcademyCourseSidebarStatus">
            <div className="top-row">
                <Label size="body-2">{localize("Status") + ":"}</Label>
                <div className={[
                    "status-label",
                    props.course.userProgress.isCompleted ?
                        "completed" :
                        props.course.userProgress.isInitialized ?
                            "initialized" :
                            ""
                ].join(" ")}>
                    <Label size="body-2" weight="medium">
                        {localize(props.course.userProgress.isCompleted ?
                            localize("Avklarad") :
                            props.course.userProgress.isInitialized ?
                                localize("IN_PROGRESS_SINGULAR") :
                                localize("EjPaborjad"))}
                    </Label>
                </div>
            </div>
            {props.course.userProgress.isInitialized && !props.course.userProgress.isCompleted && (
                <AcademyCourseProgress userProgress={props.course.userProgress} />
            )}
            {state.isLoadingAttendance && (
                <SpintrLoader />
            )}
            <div className="buttons">
                {props.displayPreview && !state.isLoadingAttendance && !props.course.userProgress.isCompleted && (
                    <PrimaryButton
                        text={localize(props.course.userProgress.isInitialized ? "CONTINUE_COURSE" : "START_COURSE")}
                        onClick={props.course.userProgress.isInitialized ? props.onOpenCourse : onAttendClick}
                    />
                )}
                {props.displayPreview && !state.isLoadingAttendance && props.course.userProgress.isInitialized && !props.course.userProgress.isCompleted && (
                    <DefaultButton
                        text={localize("CANCEL_COURSE")}
                        onClick={onCancelClick}
                    />
                )}
                {props.displayPreview && !state.isLoadingAttendance && props.course.userProgress.isCompleted && (
                    <TooltipHost
                        className="marketplace-tooltip"
                        content={state.expiredCourse ? localize("COURSE_NO_LONGER_PUBLISHED") : undefined}
                    >
                        <DefaultButton
                            text={localize("VIEW_COURSE_CONTENT")}
                            onClick={props.onOpenCourse}
                            disabled={state.expiredCourse}
                        />
                    </TooltipHost>
                )}
                {props.course.userProgress.isCompleted && (
                    <TooltipHost
                        className="marketplace-tooltip"
                        content={state.expiredCourse ? localize("COURSE_NO_LONGER_PUBLISHED") : undefined}
                    >
                        <PrimaryButton
                            text={localize("RATE_THE_COURSE")}
                            onClick={props.onReviewCourse}
                            disabled={state.expiredCourse}
                        />
                    </TooltipHost>
                )}
            </div>
            {props.displayPreview && isAdminOrEditor && !appMode && (
                <div className="buttons">
                    <Link to={`/admin/learning/${props.course.id}`}>
                        <DefaultButton text={localize("VIEW_COURSE_PARTICIPANTS")} />
                    </Link>
                    <Link to={`/admin/learning/${props.course.id}/edit`}>
                        <DefaultButton text={localize("EDIT_COURSE")} />
                    </Link>
                </div>
            )}
        </div>
    )
}

export default AcademyCourseSidebarStatus;
